// Local (no-AI) email template renderer for the Marketing builder.
// Produces email-safe HTML (table layout + inline styles) from selected products.
import { resolveImage } from "./products";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WHATSAPP = "https://wa.me/13057188718";
const PHONE = "(305) 718-8718";

export const TEMPLATES = [
  { id: "grid", name: "Product Grid", desc: "Cards in 2 columns — image, specs & quantity" },
  { id: "sheet", name: "Stock Sheet", desc: "Compact table — ideal for wholesale lists" },
  { id: "hero", name: "Single Hero", desc: "One featured product with a big image" },
  { id: "classic", name: "Classic List", desc: "Image left, details right — stacked rows" },
];

export function defaultSubject(id, products = [], lang = "en") {
  const n = products.length;
  const first = (products[0] && products[0].name) || "Refurbished IT";
  const en = {
    grid: `Fresh Stock — ${n} Certified Refurbished System${n === 1 ? "" : "s"}`,
    sheet: "Updated Inventory & Stock List",
    hero: `Featured Deal — ${first}`,
    classic: "This Week's Refurbished IT Picks",
  };
  const es = {
    grid: `Nuevo Inventario — ${n} equipo${n === 1 ? "" : "s"} reacondicionado${n === 1 ? "" : "s"}`,
    sheet: "Lista de Inventario Actualizada",
    hero: `Destacado: ${first}`,
    classic: "Selección de esta semana — IT Reacondicionado",
  };
  return (lang === "es" ? es : en)[id] || en[id];
}

function defaultHeadline(id, lang) {
  const en = {
    grid: "New Arrivals In Stock",
    sheet: "Current Inventory",
    hero: "Featured This Week",
    classic: "Available Now",
  };
  const es = {
    grid: "Nuevas Llegadas en Existencia",
    sheet: "Inventario Actual",
    hero: "Destacado Esta Semana",
    classic: "Disponible Ahora",
  };
  return (lang === "es" ? es : en)[id] || en[id];
}

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function absUrl(u, origin) {
  if (!u) return "";
  if (/^https?:\/\//.test(u)) return u;
  return `${origin || BACKEND}${u}`;
}

function imgFor(p, origin) {
  return absUrl(resolveImage(p.image_url, p.category), origin);
}

function isDark(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return true;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6;
}

export function logoUrl(brand, origin) {
  const white = brand.logo_style === "white" || (brand.logo_style !== "dark" && isDark(brand.header_bg));
  return `${origin || window.location.origin}/uploads/${white ? "tcs_logo_white.png" : "tcs_logo.png"}`;
}

function specPairs(p, max) {
  return Object.entries(p.specs || {})
    .filter(([, v]) => String(v ?? "").trim())
    .slice(0, max)
    .map(([k, v]) => `${k}: ${v}`);
}

function qtyText(p, lang) {
  if (p.quantity == null || p.quantity === "") return lang === "es" ? "En existencia" : "In stock";
  return lang === "es" ? `${p.quantity} en existencia` : `${p.quantity} in stock`;
}

function header(brand, headline) {
  return `
  <tr><td style="background:${brand.header_bg};padding:24px 28px;" align="left">
    <img src="${logoUrl(brand)}" alt="Tele-Comp Solutions" height="40" style="height:40px;display:block;border:0;margin-bottom:10px;" />
    <div style="color:#ffffff;font-size:22px;font-weight:800;font-family:Arial,Helvetica,sans-serif;">${esc(headline)}</div>
  </td></tr>`;
}

function footer(brand, lang) {
  const cta = lang === "es" ? "Cotizar por WhatsApp" : "Get a Quote on WhatsApp";
  const tag = lang === "es"
    ? "Reacondicionado certificado · Datos borrados · Probado · Envío mundial"
    : "Certified refurbished · Data-wiped · Fully tested · Worldwide shipping";
  return `
  <tr><td style="padding:24px 28px;background:${brand.footer_bg};color:${brand.footer_text};font-family:Arial,Helvetica,sans-serif;" align="center">
    <a href="${WHATSAPP}" style="display:inline-block;background:${brand.accent_color};color:#ffffff;text-decoration:none;font-weight:800;font-size:15px;padding:13px 30px;border-radius:999px;">${cta}</a>
    <div style="margin-top:16px;font-size:13px;line-height:1.6;">
      Tele-Comp Solutions · Miami, FL · ${PHONE}<br/>
      <span style="opacity:.8;">${tag}</span>
    </div>
  </td></tr>`;
}

function card(p, brand, lang, origin) {
  const specs = specPairs(p, 3);
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;background:#ffffff;">
    <tr><td style="background:#f1f5f9;" align="center"><img src="${imgFor(p, origin)}" alt="${esc(p.name)}" width="100%" style="display:block;width:100%;max-height:180px;object-fit:cover;border:0;" /></td></tr>
    <tr><td style="padding:14px 16px;font-family:Arial,Helvetica,sans-serif;">
      <div style="font-size:16px;font-weight:800;color:${brand.text_color};line-height:1.3;">${esc(p.name)}</div>
      ${p.brand ? `<div style="font-size:13px;color:#64748b;margin-top:2px;">${esc(p.brand)}${p.condition ? " · " + esc(p.condition) : ""}</div>` : (p.condition ? `<div style="font-size:13px;color:#64748b;margin-top:2px;">${esc(p.condition)}</div>` : "")}
      ${specs.length ? `<div style="font-size:13px;color:#475569;margin-top:8px;line-height:1.6;">${specs.map(esc).join("<br/>")}</div>` : ""}
      <div style="margin-top:10px;font-size:14px;font-weight:800;color:${brand.accent_color};">${esc(qtyText(p, lang))}</div>
    </td></tr>
  </table>`;
}

function gridBody(products, brand, lang, origin) {
  let rows = "";
  for (let i = 0; i < products.length; i += 2) {
    const a = products[i], b = products[i + 1];
    rows += `<tr>
      <td width="50%" valign="top" style="padding:8px;">${card(a, brand, lang, origin)}</td>
      <td width="50%" valign="top" style="padding:8px;">${b ? card(b, brand, lang, origin) : ""}</td>
    </tr>`;
  }
  return `<tr><td style="padding:16px 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>`;
}

function classicBody(products, brand, lang, origin) {
  const rows = products.map((p) => {
    const specs = specPairs(p, 4);
    return `<tr><td style="padding:8px 0;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;background:#ffffff;">
        <tr>
          <td width="140" valign="top" style="background:#f1f5f9;"><img src="${imgFor(p, origin)}" alt="${esc(p.name)}" width="140" style="display:block;width:140px;height:120px;object-fit:cover;border:0;" /></td>
          <td valign="top" style="padding:12px 16px;font-family:Arial,Helvetica,sans-serif;">
            <div style="font-size:16px;font-weight:800;color:${brand.text_color};">${esc(p.name)}</div>
            ${p.brand || p.condition ? `<div style="font-size:13px;color:#64748b;margin-top:2px;">${[p.brand, p.condition].filter(Boolean).map(esc).join(" · ")}</div>` : ""}
            ${specs.length ? `<div style="font-size:13px;color:#475569;margin-top:6px;line-height:1.6;">${specs.map(esc).join(" · ")}</div>` : ""}
            <div style="margin-top:8px;font-size:14px;font-weight:800;color:${brand.accent_color};">${esc(qtyText(p, lang))}</div>
          </td>
        </tr>
      </table>
    </td></tr>`;
  }).join("");
  return `<tr><td style="padding:16px 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>`;
}

function sheetBody(products, brand, lang, origin) {
  const th = lang === "es" ? ["Producto", "Especificaciones", "Cant."] : ["Product", "Specs", "Qty"];
  const head = `<tr style="background:${brand.header_bg};">
    ${th.map((h, i) => `<td style="padding:10px 12px;color:#ffffff;font-weight:800;font-size:13px;font-family:Arial,Helvetica,sans-serif;${i === 2 ? "text-align:right;" : ""}">${esc(h)}</td>`).join("")}
  </tr>`;
  const rows = products.map((p, idx) => {
    const specs = specPairs(p, 4).join(" · ");
    const bg = idx % 2 ? "#f8fafc" : "#ffffff";
    const qty = p.quantity == null || p.quantity === "" ? "—" : p.quantity;
    return `<tr style="background:${bg};">
      <td style="padding:10px 12px;font-family:Arial,Helvetica,sans-serif;border-top:1px solid #e2e8f0;">
        <div style="font-weight:700;color:${brand.text_color};font-size:14px;">${esc(p.name)}</div>
        ${p.brand ? `<div style="font-size:12px;color:#94a3b8;">${esc(p.brand)}${p.condition ? " · " + esc(p.condition) : ""}</div>` : ""}
      </td>
      <td style="padding:10px 12px;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#475569;border-top:1px solid #e2e8f0;">${esc(specs) || "—"}</td>
      <td style="padding:10px 12px;font-family:Arial,Helvetica,sans-serif;font-weight:800;color:${brand.accent_color};text-align:right;border-top:1px solid #e2e8f0;">${esc(qty)}</td>
    </tr>`;
  }).join("");
  return `<tr><td style="padding:16px 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;">${head}${rows}</table></td></tr>`;
}

function heroBody(products, brand, lang, origin) {
  const p = products[0];
  const rest = products.slice(1);
  const specs = specPairs(p, 6);
  let html = `<tr><td style="padding:16px 20px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:14px;overflow:hidden;background:#ffffff;">
      <tr><td style="background:#f1f5f9;" align="center"><img src="${imgFor(p, origin)}" alt="${esc(p.name)}" width="100%" style="display:block;width:100%;max-height:300px;object-fit:cover;border:0;" /></td></tr>
      <tr><td style="padding:20px 22px;font-family:Arial,Helvetica,sans-serif;">
        <div style="font-size:22px;font-weight:800;color:${brand.text_color};line-height:1.25;">${esc(p.name)}</div>
        ${p.brand || p.condition ? `<div style="font-size:14px;color:#64748b;margin-top:4px;">${[p.brand, p.condition].filter(Boolean).map(esc).join(" · ")}</div>` : ""}
        ${specs.length ? `<div style="font-size:14px;color:#475569;margin-top:12px;line-height:1.7;">${specs.map(esc).join("<br/>")}</div>` : ""}
        <div style="margin-top:14px;font-size:18px;font-weight:800;color:${brand.accent_color};">${esc(qtyText(p, lang))}</div>
      </td></tr>
    </table>
  </td></tr>`;
  if (rest.length) html += gridBody(rest, brand, lang, origin);
  return html;
}

export function renderTemplate({ templateId, products = [], brand, headline, lang = "en", origin }) {
  const safeBrand = {
    logo_style: "auto", header_bg: "#0f172a", accent_color: "#16a34a",
    text_color: "#1f2937", footer_bg: "#111827", footer_text: "#e2e8f0", ...(brand || {}),
  };
  const head = headline || defaultHeadline(templateId, lang);
  const builders = { grid: gridBody, sheet: sheetBody, hero: heroBody, classic: classicBody };
  const body = (builders[templateId] || gridBody)(products, safeBrand, lang, origin);
  return `<!DOCTYPE html>
<html lang="${lang}"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" /></head>
<body style="margin:0;padding:0;background:#e9eef3;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#e9eef3;padding:20px 0;"><tr><td align="center">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;border-radius:16px;overflow:hidden;">
      ${header(safeBrand, head)}
      ${body}
      ${footer(safeBrand, lang)}
    </table>
  </td></tr></table>
</body></html>`;
}

// Extract product image srcs from generated HTML (excludes the logo) for the swap tool.
export function extractProductImages(html) {
  if (!html) return [];
  const out = [];
  const re = /<img[^>]+src=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html))) {
    const src = m[1];
    if (/tcs_logo/i.test(src)) continue;
    if (!out.includes(src)) out.push(src);
  }
  return out;
}
